import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuthState } from '../hooks/useAuthState';
import { generateInviteToken, generateInviteURL } from '../lib/invites';
import { canSponsorMember } from '../lib/permissions';
import { createGroupSponsorNotification } from '../lib/notifications';
import { getGroupMembers } from '../lib/firestore/groups';
import '../styles/InviteModal.css';

const SponsorModal = ({ isOpen, onClose, groupId, groupName, userId, userRole, groupRules }) => {
  const { user: currentUser } = useAuthState();
  const [contacts, setContacts] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sponsoring, setSponsoring] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sponsorCheck, setSponsorCheck] = useState(null);
  const [activeTab, setActiveTab] = useState('contacts');
  const [groupMembers, setGroupMembers] = useState([]);
  const [inviteStatuses, setInviteStatuses] = useState({}); // { contactId: 'sent' | 'member' | null }

  useEffect(() => {
    if (isOpen && currentUser?.uid && groupId) {
      console.log('🔵 [SponsorModal] useEffect triggered - loading contacts and checking eligibility');
      loadGroupMembers();
      loadContactsAndFriends();
      checkSponsorEligibility();
    } else {
      console.log('🔵 [SponsorModal] useEffect - waiting for conditions:', {
        isOpen,
        hasCurrentUser: !!currentUser?.uid,
        groupId,
      });
    }
  }, [isOpen, currentUser?.uid, groupId]);

  const loadGroupMembers = async () => {
    if (!groupId) return;
    try {
      const members = await getGroupMembers(groupId);
      setGroupMembers(members);
      console.log('🔵 [SponsorModal] Group members loaded:', members.length);
    } catch (error) {
      console.error('🔴 [SponsorModal] Error loading group members:', error);
    }
  };

  const checkInviteStatuses = async (contacts, friends) => {
    if (!currentUser?.uid || !groupId) return;

    // Reload group members to ensure we have the latest data
    let members = groupMembers;
    if (members.length === 0) {
      try {
        members = await getGroupMembers(groupId);
        setGroupMembers(members);
      } catch (error) {
        console.error('🔴 [SponsorModal] Error loading members for status check:', error);
        return;
      }
    }

    const statuses = {};
    const memberUserIds = new Set(members.map(m => m.userId));
    
    // Check for existing notifications from current user
    const notificationsRef = collection(db, 'notifications');
    const notificationsQuery = query(
      notificationsRef,
      where('fromUserId', '==', currentUser.uid),
      where('groupId', '==', groupId),
      where('type', 'in', ['group_sponsor', 'event_invite'])
    );
    
    try {
      const notificationsSnapshot = await getDocs(notificationsQuery);
      const sentInvites = new Set();
      notificationsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.userId) {
          sentInvites.add(data.userId);
        }
      });

      // Check contacts
      contacts.forEach((contact) => {
        if (contact.userId) {
          if (memberUserIds.has(contact.userId)) {
            statuses[contact.id] = 'member';
          } else if (sentInvites.has(contact.userId)) {
            statuses[contact.id] = 'sent';
          }
        }
      });

      // Check friends
      friends.forEach((friend) => {
        if (friend.userId) {
          if (memberUserIds.has(friend.userId)) {
            statuses[friend.userId] = 'member';
          } else if (sentInvites.has(friend.userId)) {
            statuses[friend.userId] = 'sent';
          }
        }
      });

      setInviteStatuses(statuses);
      console.log('🔵 [SponsorModal] Invite statuses:', statuses);
    } catch (error) {
      console.error('🔴 [SponsorModal] Error checking invite statuses:', error);
    }
  };

  const checkSponsorEligibility = async () => {
    if (!userId || !groupId) return;
    try {
      const result = await canSponsorMember(groupId, userId);
      setSponsorCheck(result);
    } catch (error) {
      console.error('Error checking sponsor eligibility:', error);
      setSponsorCheck({ can: false, reason: 'Error checking eligibility' });
    }
  };

  const loadContactsAndFriends = async () => {
    // Always use currentUser.uid to ensure it matches the authenticated user for Firestore rules
    if (!currentUser?.uid) {
      console.error('🔴 [SponsorModal] No authenticated user available');
      setLoading(false);
      return;
    }
    const effectiveUserId = currentUser.uid;
    setLoading(true);
    try {
      // Load contacts - ensure we're using the correct path
      console.log('🔵 [SponsorModal] Loading contacts for userId:', effectiveUserId);
      console.log('🔵 [SponsorModal] Current auth user object:', currentUser);
      console.log('🔵 [SponsorModal] Current auth user uid:', currentUser?.uid);
      const contactsRef = collection(db, 'users', effectiveUserId, 'contacts');
      console.log('🔵 [SponsorModal] Contacts collection path:', contactsRef.path);
      const contactsSnapshot = await getDocs(contactsRef);
      console.log('🔵 [SponsorModal] Contacts loaded:', contactsSnapshot.docs.length);
      const contactsData = await Promise.all(
        contactsSnapshot.docs.map(async (contactDoc) => {
          const contact = { id: contactDoc.id, ...contactDoc.data() };
          // Check if contact is a user
          if (contact.phoneE164) {
            const phoneDigits = contact.phoneE164.replace(/\D/g, '');
            const userQuery = query(
              collection(db, 'users'),
              where('phone', '==', contact.phoneE164)
            );
            const userSnapshot = await getDocs(userQuery);
            if (userSnapshot.empty) {
              const phoneNumberQuery = query(
                collection(db, 'users'),
                where('phoneNumber', '==', phoneDigits)
              );
              const phoneNumberSnapshot = await getDocs(phoneNumberQuery);
              if (!phoneNumberSnapshot.empty) {
                const userDoc = phoneNumberSnapshot.docs[0];
                contact.userId = userDoc.id;
                contact.userData = userDoc.data();
              }
            } else {
              const userDoc = userSnapshot.docs[0];
              contact.userId = userDoc.id;
              contact.userData = userDoc.data();
            }
          }
          return contact;
        })
      );

      // Load friends - use queries instead of getting all documents
      const friendshipsRef = collection(db, 'friendships');
      const userFriendships = [];
      
      // Query friendships where user is 'a'
      const q1 = query(friendshipsRef, where('a', '==', effectiveUserId));
      const snapshot1 = await getDocs(q1);
      snapshot1.forEach((doc) => {
        const data = doc.data();
        if (data.b) {
          userFriendships.push(data.b);
        }
      });
      
      // Query friendships where user is 'b'
      const q2 = query(friendshipsRef, where('b', '==', effectiveUserId));
      const snapshot2 = await getDocs(q2);
      snapshot2.forEach((doc) => {
        const data = doc.data();
        if (data.a) {
          userFriendships.push(data.a);
        }
      });

      const friendsData = await Promise.all(
        userFriendships.map(async (friendId) => {
          const friendDoc = await getDoc(doc(db, 'users', friendId));
          if (friendDoc.exists()) {
            return { userId: friendId, ...friendDoc.data() };
          }
          return null;
        })
      );

      const validContacts = contactsData.filter(c => c);
      const validFriends = friendsData.filter(f => f);
      console.log('🔵 [SponsorModal] Setting contacts:', validContacts.length);
      console.log('🔵 [SponsorModal] Setting friends:', validFriends.length);
      console.log('🔵 [SponsorModal] Contacts data:', validContacts);
      console.log('🔵 [SponsorModal] Friends data:', validFriends);
      setContacts(validContacts);
      setFriends(validFriends);
      
      // Check invite statuses after contacts/friends are loaded
      // Use setTimeout to ensure groupMembers state has updated
      setTimeout(() => {
        checkInviteStatuses(validContacts, validFriends);
      }, 100);
    } catch (error) {
      console.error('🔴 [SponsorModal] Error loading contacts and friends:', error);
    } finally {
      console.log('🔵 [SponsorModal] Setting loading to false');
      setLoading(false);
    }
  };

  const handleSponsor = async (contact) => {
    // Early return if required values are missing
    if (!groupId || !userId || sponsoring[contact.id]) {
      console.error('🔴 [SponsorModal] Missing required values:', { groupId, userId, contactId: contact.id });
      return;
    }

    // Check if user can sponsor (if not owner/cohost)
    if (userRole !== 'owner' && userRole !== 'cohost') {
      if (!sponsorCheck?.can) {
        alert(`Cannot sponsor: ${sponsorCheck?.reason || 'Not eligible'}`);
        return;
      }
    }

    setSponsoring({ ...sponsoring, [contact.id]: true });
    try {
      // Double-check userId is still valid (defensive programming)
      const validUserId = userId || currentUser?.uid;
      if (!validUserId || typeof validUserId !== 'string' || validUserId.trim() === '') {
        console.error('🔴 [SponsorModal] No valid userId available:', { userId, currentUserId: currentUser?.uid });
        alert('Error: User ID is missing. Please try again.');
        setSponsoring({ ...sponsoring, [contact.id]: false });
        return;
      }

      // Build metadata object with only defined, non-null values
      const metadata = {
        sponsorId: String(validUserId).trim(),
        role: 'member'
      };

      // Final validation - ensure no undefined/null values
      if (!metadata.sponsorId || !metadata.role) {
        console.error('🔴 [SponsorModal] Invalid metadata:', metadata);
        alert('Error: Invalid metadata. Please try again.');
        setSponsoring({ ...sponsoring, [contact.id]: false });
        return;
      }

      console.log('🔵 [SponsorModal] Creating invite with metadata:', metadata);
      console.log('🔵 [SponsorModal] Valid userId:', validUserId);
      console.log('🔵 [SponsorModal] GroupId:', groupId);

      // Generate invite token for group join with sponsor metadata
      const token = await generateInviteToken(
        { kind: 'group_join', id: groupId },
        validUserId,
        { 
          oneTime: false, 
          expiresIn: 7 * 24 * 60 * 60, // 7 days
          metadata
        }
      );

      const inviteURL = generateInviteURL(token);

      // If contact is a Hotelia user, create a notification
      // Check both contact.userId and contact.userData?.userId (for friends)
      const targetUserId = contact.userId || contact.userData?.userId || (contact.userData && contact.id);
      
      if (targetUserId && targetUserId !== validUserId) {
        try {
          // Get sponsor name
          const sponsorDoc = await getDoc(doc(db, 'users', validUserId));
          const sponsorData = sponsorDoc.data();
          const sponsorName = sponsorData
            ? `${sponsorData.firstName || ''} ${sponsorData.lastName || ''}`.trim() || 'Someone'
            : 'Someone';

          // Create notification
          console.log('🔵 [SponsorModal] Creating notification for contact:', {
            contactUserId: targetUserId,
            contactId: contact.id,
            groupId,
            groupName,
            sponsorName,
            token,
            sponsorId: validUserId
          });
          
          await createGroupSponsorNotification(
            targetUserId,
            groupId,
            groupName || 'Group',
            sponsorName,
            token,
            validUserId
          );
          console.log('✅ [SponsorModal] Notification created successfully');
          alert(`✅ ${contact.name || contact.userData?.firstName || 'User'} has been notified about the group invitation!`);
          
          // Update invite status
          const statusKey = contact.id || targetUserId;
          setInviteStatuses(prev => ({
            ...prev,
            [statusKey]: 'sent'
          }));
        } catch (notifError) {
          console.error('🔴 [SponsorModal] Error creating notification:', notifError);
          console.error('🔴 [SponsorModal] Error details:', {
            code: notifError.code,
            message: notifError.message,
            stack: notifError.stack
          });
          // Fallback to clipboard
          await navigator.clipboard.writeText(inviteURL);
          alert(`✅ Sponsor link copied to clipboard!\n\nShare this link with ${contact.name || contact.userData?.firstName || 'them'} to join the group.`);
        }
      } else {
        // Not a user - copy to clipboard
        try {
          await navigator.clipboard.writeText(inviteURL);
          alert(`✅ Sponsor link copied to clipboard!\n\nShare this link with ${contact.name || contact.userData?.firstName || 'them'} to join the group.`);
        } catch (err) {
          // Fallback: show link
          prompt('Copy this sponsor link:', inviteURL);
        }
      }
    } catch (error) {
      console.error('Error creating sponsor invite:', error);
      alert('Failed to create sponsor invite. Please try again.');
    } finally {
      setSponsoring({ ...sponsoring, [contact.id]: false });
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const search = searchTerm.toLowerCase();
    const name = (contact.name || contact.userData?.firstName || '').toLowerCase();
    const phone = (contact.phoneE164 || '').toLowerCase();
    const email = (contact.email || contact.userData?.email || '').toLowerCase();
    return name.includes(search) || phone.includes(search) || email.includes(search);
  });

  const filteredFriends = friends.filter(friend => {
    const search = searchTerm.toLowerCase();
    const name = `${friend.firstName || ''} ${friend.lastName || ''}`.toLowerCase();
    const email = (friend.email || '').toLowerCase();
    return name.includes(search) || email.includes(search);
  });

  const isOwnerOrCohost = userRole === 'owner' || userRole === 'cohost';
  const canSponsor = isOwnerOrCohost || sponsorCheck?.can;

  // Debug logging
  console.log('🔵 [SponsorModal] Render state:', {
    isOpen,
    loading,
    canSponsor,
    isOwnerOrCohost,
    sponsorCheck,
    contactsCount: contacts.length,
    friendsCount: friends.length,
    activeTab,
    filteredContactsCount: filteredContacts.length,
    filteredFriendsCount: filteredFriends.length,
  });

  if (!isOpen) return null;

  return (
    <div className="invite-modal-overlay" onClick={onClose}>
      <div className="invite-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="invite-modal-header">
          <h2>Sponsor to {groupName || 'Group'}</h2>
          <button className="invite-modal-close" onClick={onClose}>×</button>
        </div>

        {/* Sponsor Rules Info */}
        {groupRules && (
          <div className="sponsor-rules-info">
            {isOwnerOrCohost ? (
              <p className="sponsor-info-text">
                As {userRole === 'owner' ? 'owner' : 'cohost'}, you can sponsor unlimited members.
              </p>
            ) : (
              <>
                {groupRules.maxSponsorsPerMember && (
                  <p className="sponsor-info-text">
                    You can sponsor up to {groupRules.maxSponsorsPerMember} {groupRules.maxSponsorsPerMember === 1 ? 'person' : 'people'}.
                  </p>
                )}
                {groupRules.sponsorEligibility?.requireOneEvent && (
                  <p className="sponsor-info-text">
                    ⚠️ You must attend one event before sponsoring others.
                  </p>
                )}
                {groupRules.sponsorAccountabilityText && (
                  <div className="sponsor-accountability">
                    <strong>Accountability:</strong> {groupRules.sponsorAccountabilityText}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {!canSponsor && (
          <div className="sponsor-error">
            <p>You cannot sponsor members to this group.</p>
            {sponsorCheck?.reason && <small>{sponsorCheck.reason}</small>}
          </div>
        )}

        <div className="invite-modal-search">
          <input
            type="text"
            placeholder="Search contacts or friends..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="invite-search-input"
            disabled={!canSponsor}
          />
        </div>

        {/* Tabs */}
        {canSponsor && (
          <div className="invite-modal-tabs">
            <button
              className={`invite-tab ${activeTab === 'contacts' ? 'active' : ''}`}
              onClick={() => setActiveTab('contacts')}
            >
              Contacts ({contacts.length})
            </button>
            <button
              className={`invite-tab ${activeTab === 'friends' ? 'active' : ''}`}
              onClick={() => setActiveTab('friends')}
            >
              Friends ({friends.length})
            </button>
          </div>
        )}

        {loading ? (
          <div className="invite-modal-loading">Loading contacts...</div>
        ) : canSponsor ? (
          <div className="invite-modal-body">
            {activeTab === 'contacts' ? (
              filteredContacts.length > 0 ? (
                <div className="invite-list">
                  {filteredContacts.map((contact) => {
                    const status = inviteStatuses[contact.id];
                    const isMember = status === 'member';
                    const inviteSent = status === 'sent';
                    
                    return (
                      <div key={contact.id} className="invite-item">
                        <div className="invite-item-info">
                          <div className="invite-item-name">
                            {contact.name || contact.userData?.firstName || 'Unknown'}
                            {contact.userData && (
                              <span className="invite-item-badge">Hotelia User</span>
                            )}
                          </div>
                          <div className="invite-item-details">
                            {contact.phoneE164 && <span>📱 {contact.phoneE164}</span>}
                            {contact.email && <span>✉️ {contact.email}</span>}
                          </div>
                        </div>
                        {isMember ? (
                          <span className="invite-status-badge status-member">Already in group</span>
                        ) : inviteSent ? (
                          <span className="invite-status-badge status-sent">Invite sent</span>
                        ) : (
                          <button
                            className="btn-invite"
                            onClick={() => handleSponsor(contact)}
                            disabled={sponsoring[contact.id]}
                          >
                            {sponsoring[contact.id] ? 'Sponsoring...' : 'Sponsor'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="invite-empty">
                  <p>No contacts found{searchTerm && ` matching "${searchTerm}"`}.</p>
                  <small>Add contacts in the Contacts page to sponsor them to groups.</small>
                </div>
              )
            ) : (
              filteredFriends.length > 0 ? (
                <div className="invite-list">
                  {filteredFriends.map((friend) => {
                    const status = inviteStatuses[friend.userId];
                    const isMember = status === 'member';
                    const inviteSent = status === 'sent';
                    
                    return (
                      <div key={friend.userId} className="invite-item">
                        <div className="invite-item-info">
                          <div className="invite-item-name">
                            {`${friend.firstName || ''} ${friend.lastName || ''}`.trim() || 'Friend'}
                            <span className="invite-item-badge">Friend</span>
                          </div>
                          <div className="invite-item-details">
                            {friend.email && <span>✉️ {friend.email}</span>}
                          </div>
                        </div>
                        {isMember ? (
                          <span className="invite-status-badge status-member">Already in group</span>
                        ) : inviteSent ? (
                          <span className="invite-status-badge status-sent">Invite sent</span>
                        ) : (
                          <button
                            className="btn-invite"
                            onClick={() => handleSponsor({ id: friend.userId, userId: friend.userId, userData: friend })}
                            disabled={sponsoring[friend.userId]}
                          >
                            {sponsoring[friend.userId] ? 'Sponsoring...' : 'Sponsor'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="invite-empty">
                  <p>No friends found{searchTerm && ` matching "${searchTerm}"`}.</p>
                  <small>Add friends in the Contacts page to sponsor them to groups.</small>
                </div>
              )
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SponsorModal;

